import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useSession } from '@/hooks/useSession';
import { signOut } from '@/lib/auth-actions';
import { getDefaultRouteForRole } from '@/lib/auth-routing';
import { safeRedirectTarget, POST_AUTH_REDIRECT_STORAGE_KEY } from '@/lib/safe-redirect';
import { getSupabaseClient } from '@/lib/supabase';

// How long we wait for a session to materialise from the URL hash before
// surfacing an error UI. The Supabase client's detectSessionInUrl runs
// synchronously on mount; this only catches genuinely stuck callbacks.
const SESSION_TIMEOUT_MS = 5_000;

type CallbackError = 'timeout' | 'profile';

export function AuthCallbackPage() {
  const { t } = useTranslation(['auth']);
  const navigate = useNavigate();
  const { loading, session } = useSession();
  const [error, setError] = useState<CallbackError | null>(null);

  useEffect(() => {
    if (loading) return;

    if (!session) {
      const timer = setTimeout(() => setError('timeout'), SESSION_TIMEOUT_MS);
      return () => clearTimeout(timer);
    }

    let cancelled = false;
    void (async () => {
      try {
        const supabase = getSupabaseClient();
        const { data: profile, error: profileErr } = await supabase
          .from('users')
          .select('auth_provider, role')
          .eq('id', session.user.id)
          .single();

        if (cancelled) return;
        if (profileErr || !profile) {
          setError('profile');
          return;
        }

        // D1 collision safety net — Supabase Auth's "link email identities"
        // toggle should reject this case upstream, but if config drift or
        // a future toggle change lets the OAuth session land on an existing
        // email/password account, refuse and bounce to /signin so the user
        // signs in with their password instead.
        if (profile.auth_provider === 'email') {
          await signOut().catch(() => undefined);
          if (!cancelled) {
            navigate('/signin?error=oauth_email_taken', { replace: true });
          }
          return;
        }

        // Best-effort signin_oauth audit. Idempotent server-side: returns
        // false (no INSERT) when the AFTER INSERT trigger already wrote a
        // signup_oauth row in the last 30s. Failure here must not block
        // the redirect — the audit gap is recoverable, blocking the user
        // is not.
        await supabase.rpc('record_oauth_signin').then(
          () => undefined,
          () => undefined,
        );

        if (cancelled) return;
        // Honour a post-login redirect carried across the OAuth round-trip
        // (e.g. a deal page + resume action) for buyers; admins always land on
        // their role home. `safeRedirectTarget` guards against open-redirect.
        const roleDefault = getDefaultRouteForRole(profile.role);
        const stored = sessionStorage.getItem(POST_AUTH_REDIRECT_STORAGE_KEY);
        sessionStorage.removeItem(POST_AUTH_REDIRECT_STORAGE_KEY);
        const target =
          profile.role === 'buyer' && stored
            ? safeRedirectTarget(stored, roleDefault)
            : roleDefault;
        navigate(target, { replace: true });
      } catch {
        if (!cancelled) setError('profile');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, session, navigate]);

  if (error) {
    return (
      <section className="m5-shell">
        <h1 className="m5-shell__title">{t('auth:callback.errorTitle')}</h1>
        <p className="m5-shell__lead">{t('auth:callback.errorLead')}</p>
        <div className="m5-card">
          <button
            type="button"
            className="m5-btn m5-btn--primary"
            onClick={() => navigate('/signin', { replace: true })}
          >
            {t('auth:callback.backToSignIn')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="m5-shell" aria-busy="true">
      <h1 className="m5-shell__title">{t('auth:callback.loadingTitle')}</h1>
      <p className="m5-shell__lead">{t('auth:callback.loadingLead')}</p>
    </section>
  );
}
